import { useEffect } from 'react';

// iOS: Tipp auf die Statusleiste scrollt nur das Dokument – das Portal scrollt aber in <main>.
// Trick: Dokument um 2px scrollbar machen und bei scrollY=1 „parken“. Springt es auf 0 (nur per Statusleisten-Tipp möglich,
// weil der Layout-Root fixed ist), scrollen wir <main> nach oben und parken wieder.
export const isIOS = () => typeof navigator !== 'undefined'
  && (/iP(hone|ad|od)/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));

const isStandalone = () => (typeof navigator !== 'undefined' && navigator.standalone === true)
  || (typeof window !== 'undefined' && window.matchMedia?.('(display-mode: standalone)').matches);

// Home-Bildschirm-App: WebKit meldet nach Tastatur/Viewport-Wechsel manchmal einen zu kleinen Layout-Viewport
// („lying viewport“, bugs.webkit.org 237961/292603) → fixed-Root endet oberhalb des Bildschirmrands (weißer Streifen).
// Gegenmaßnahme: Reflow erzwingen, danach Root-Höhe explizit auf die physische Bildschirmhöhe setzen.
const fixStandaloneViewport = () => {
  const root = document.querySelector('.portal-app');
  if (!root) return;
  const portrait = window.matchMedia('(orientation: portrait)').matches;
  const expected = portrait ? Math.max(window.screen.height, window.screen.width) : Math.min(window.screen.height, window.screen.width);
  if (window.innerHeight < expected - 1) {
    root.style.display = 'none';
    void root.offsetHeight;
    root.style.display = '';
    if (window.innerHeight < expected - 1) {
      root.style.height = `${expected}px`;
      root.style.bottom = 'auto';
      return;
    }
  }
  root.style.height = '';
  root.style.bottom = '';
};

export default function useIosStatusBarTap(scrollRef, ready = true) {
  useEffect(() => {
    if (!isIOS() || !ready) return undefined;
    const html = document.documentElement;
    html.classList.add('ios-statustap');
    const standalone = isStandalone();
    const park = () => { if (window.scrollY !== 1) window.scrollTo(0, 1); };
    const settle = () => { park(); if (standalone) fixStandaloneViewport(); };
    const onScroll = () => {
      if (window.scrollY <= 0) {
        const tag = document.activeElement?.tagName;
        if (tag !== 'INPUT' && tag !== 'TEXTAREA' && tag !== 'SELECT') {
          scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
          document.querySelectorAll('[data-ios-scroll-top]').forEach(el => el.scrollTo({ top: 0, behavior: 'smooth' }));
        }
        requestAnimationFrame(park);
      } else if (window.scrollY > 1) {
        requestAnimationFrame(park);
      }
    };
    const onFocusOut = () => setTimeout(settle, 150);
    // Parken mit Wiederholung – das Dokument wird erst scrollbar, wenn Layout + Sentinel stehen
    const timers = [0, 200, 800, 2000].map(ms => setTimeout(settle, ms));
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', settle);
    window.addEventListener('orientationchange', onFocusOut);
    window.addEventListener('pageshow', settle);
    window.visualViewport?.addEventListener('resize', settle);
    document.addEventListener('focusout', onFocusOut);
    return () => {
      timers.forEach(clearTimeout);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', settle);
      window.removeEventListener('orientationchange', onFocusOut);
      window.removeEventListener('pageshow', settle);
      window.visualViewport?.removeEventListener('resize', settle);
      document.removeEventListener('focusout', onFocusOut);
      const root = document.querySelector('.portal-app');
      if (root) { root.style.height = ''; root.style.bottom = ''; }
      html.classList.remove('ios-statustap');
    };
  }, [scrollRef, ready]);
}
